import { Empty, Tabs } from 'antd';
import React, { FC, useMemo } from 'react';
import { useTranslate } from '@refinedev/core';
import { Validation } from './validation';
import { Structure as SubmissionStructure } from './structure';
import { Title } from './title';
import { Accordion } from '../../../ui/accordion';

type Status = {
    id: number;
    code: string;
    name: string;
};

interface SubmissionsProps {
    submissions: Array<Submission>;
    status: Status;
    [key: string]: any;
}

interface ECTDValidation {
    status?: string;
    code: string;
    category: string;
    comment: string;
    criteria: string;
    details?: string | null;
}

interface Structure {
    title: string;
    file_name: string;
    ectd_path: string;
    operation: string;
    uploaded_at: string | null;
    upload_code: number | null;
    upload_text: string | null;
}

interface File {
    created_at: string;
    extension: string;
    name: string;
    size: string;
}

type Submission = {
    created_at: string;
    sequence: string;
    ectd_validation: ECTDValidation[];
    structure: Structure[];
    status: Status;
    file?: File;
};

const { TabPane } = Tabs;

export const Submissions: FC<SubmissionsProps> = ({
    submissions,
    status,
    ...props
}) => {
    const t = useTranslate();

    const normalizedSubmissions = useMemo(
        () => (Array.isArray(submissions) ? submissions : []),
        [submissions]
    );

    const accordionItems = useMemo(
        () =>
            normalizedSubmissions.map(
                (submission: Submission, index: number) => {
                    const {
                        created_at,
                        file,
                        sequence,
                        ectd_validation,
                        structure,
                        status: submissionStatus,
                    } = submission;

                    const key = sequence ?? index;

                    return {
                        key,
                        header: (
                            <Title
                                createdAt={created_at}
                                sequence={sequence}
                                status={submissionStatus ?? status}
                                fileName={file?.name}
                            />
                        ),
                        content: (
                            <Tabs
                                defaultActiveKey="validation"
                                onChange={() => {}}
                            >
                                <TabPane
                                    tab={t('applications.titles.validation')}
                                    key="validation"
                                >
                                    <Validation
                                        data={ectd_validation}
                                        group="applications.fields"
                                        scroll={{
                                            x: 500,
                                        }}
                                    />
                                </TabPane>
                                <TabPane
                                    tab={t('applications.titles.structure')}
                                    key="structure"
                                >
                                    <SubmissionStructure
                                        data={structure}
                                        group="applications.fields"
                                        scroll={{
                                            x: 500,
                                        }}
                                    />
                                </TabPane>
                            </Tabs>
                        ),
                    };
                }
            ),
        [normalizedSubmissions, status, t]
    );

    if (accordionItems.length === 0) {
        return <Empty description={t('applications.titles.no_data')} />;
    }

    return <Accordion items={accordionItems} />;
};
